import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User } from '../types';
import { AuthService } from '../firebase/auth';
import { FirestoreService } from '../firebase/firestore';

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<boolean>;
  register: (email: string, password: string, name: string, phone: string, address: string, city: string) => Promise<boolean>;
  logout: () => void;
  refreshCustomerSession: () => void;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    console.log('🔄 Setting up Firebase auth state listener...');
    
    // Check for customer session first (temporary sessions)
    const checkCustomerSession = () => {
      const customerSession = localStorage.getItem('bookfood_customer_session');
      if (customerSession) {
        try {
          const session = JSON.parse(customerSession);
          console.log('👤 AuthContext: Found customer session:', session);
          
          // Create a user object from customer session
          const customerUser: User = {
            id: session.id,
            email: session.email,
            name: session.name,
            role: 'customer',
            phone: session.phone,
            customerId: session.id, // Use session ID as customerId
            address: session.address || '',
            city: session.orderType === 'delivery' ? 'Delivery' : 'Dine-in'
          };
          
          console.log('✅ AuthContext: Created user from customer session:', customerUser);
          setUser(customerUser);
          setIsLoading(false);
          return true;
        } catch (error) {
          console.error('❌ AuthContext: Error parsing customer session:', error);
          localStorage.removeItem('bookfood_customer_session');
        }
      }
      return false;
    };

    // Check customer session first
    if (checkCustomerSession()) {
      return () => {}; // Return cleanup function
    }
    
    // Listen to Firebase auth state changes
    const unsubscribe = AuthService.onAuthStateChanged(async (firebaseUser) => {
      console.log('🔔 Auth state changed:', firebaseUser ? 'User logged in' : 'User logged out');
      
      if (firebaseUser) {
        try {
          console.log('👤 Firebase user detected:', firebaseUser.uid);
          console.log('📊 Firebase user details:', {
            uid: firebaseUser.uid,
            email: firebaseUser.email,
            displayName: firebaseUser.displayName,
            emailVerified: firebaseUser.emailVerified
          });
          
          // Get user data from Firestore
          console.log('🔍 AuthContext: Fetching user data from Firestore...');
          const userData = await FirestoreService.getUser(firebaseUser.uid);
          
          if (userData) {
            console.log('✅ AuthContext: User data loaded from Firestore:');
            console.log('📊 AuthContext: User data:', JSON.stringify(userData, null, 2));
            setUser(userData);
            
            // Store in localStorage for persistence
            console.log('💾 AuthContext: Saving user data to localStorage...');
            localStorage.setItem('bookfood_user', JSON.stringify(userData));
            console.log('✅ AuthContext: User data saved to localStorage');
            
            // Verify localStorage data
            const storedUser = localStorage.getItem('bookfood_user');
            console.log('🔍 AuthContext: Verifying localStorage data:', storedUser ? 'Data stored' : 'No data');
          } else {
            console.log('⚠️ AuthContext: No user data found in Firestore for:', firebaseUser.uid);
            console.log('⚠️ AuthContext: This might indicate Firestore database is not created yet');
          }
        } catch (error) {
          console.error('❌ AuthContext: Error getting user data:', error);
          console.error('❌ AuthContext: Error details:', {
            uid: firebaseUser.uid,
            error: error instanceof Error ? error.message : 'Unknown error'
          });
        }
      } else {
        console.log('🚪 AuthContext: No user logged in, clearing state');
        setUser(null);
        localStorage.removeItem('bookfood_user');
        console.log('✅ AuthContext: User state cleared');
      }
      setIsLoading(false);
      console.log('✅ Auth state listener setup complete');
    });

    return () => {
      console.log('🔄 Cleaning up auth state listener');
      unsubscribe();
    };
  }, []);

  const login = async (email: string, password: string): Promise<boolean> => {
    console.log('🔐 AuthContext: Starting login process...', { email });
    setIsLoading(true);
    
    try {
      const result = await AuthService.login(email, password);
      if (result.success && result.user) {
        console.log('✅ AuthContext: Login successful, setting user state');
        setUser(result.user);
        return true;
      } else {
        console.error('❌ AuthContext: Login failed:', result.error);
        return false;
      }
    } catch (error) {
      console.error('❌ AuthContext: Login error:', error);
      return false;
    } finally {
      setIsLoading(false);
      console.log('🔐 AuthContext: Login process completed');
    }
  };

  const register = async (email: string, password: string, name: string, phone: string, address: string, city: string): Promise<boolean> => {
    console.log('📝 AuthContext: Starting registration process...', { email, name });
    setIsLoading(true);
    
    try {
      const result = await AuthService.register(email, password, name, phone, address, city);
      if (result.success && result.user) {
        console.log('✅ AuthContext: Registration successful, setting user state');
        setUser(result.user);
        
        // Dispatch custom event to refresh customers in DataContext
        if (result.user.role === 'customer') {
          console.log('🔄 AuthContext: Dispatching customer refresh event');
          window.dispatchEvent(new CustomEvent('customerRegistered'));
        }
        
        return true;
      } else {
        console.error('❌ AuthContext: Registration failed:', result.error);
        return false;
      }
    } catch (error) {
      console.error('❌ AuthContext: Registration error:', error);
      return false;
    } finally {
      setIsLoading(false);
      console.log('📝 AuthContext: Registration process completed');
    }
  };

  const logout = async () => {
    console.log('🚪 AuthContext: Starting logout process...');
    try {
      await AuthService.logout();
      setUser(null);
      localStorage.removeItem('bookfood_user');
      localStorage.removeItem('bookfood_customer_session');
      // Clear table selection on logout
      localStorage.removeItem('bookfood_selectedTableId');
      console.log('✅ AuthContext: Logout successful, table selection cleared');
    } catch (error) {
      console.error('❌ AuthContext: Logout error:', error);
      // Still clear local state even if Firebase logout fails
      setUser(null);
      localStorage.removeItem('bookfood_user');
      localStorage.removeItem('bookfood_customer_session');
      localStorage.removeItem('bookfood_selectedTableId');
      console.log('⚠️ AuthContext: Local state cleared despite logout error');
    }
  };

  const refreshCustomerSession = () => {
    console.log('🔄 AuthContext: Refreshing customer session...');
    const customerSession = localStorage.getItem('bookfood_customer_session');
    if (customerSession) {
      try {
        const session = JSON.parse(customerSession);
        console.log('👤 AuthContext: Refreshing user from customer session:', session);
        
        // Create a user object from customer session
        const customerUser: User = {
          id: session.id,
          email: session.email,
          name: session.name,
          role: 'customer',
          phone: session.phone,
          customerId: session.id, // Use session ID as customerId
          address: session.address || '',
          city: session.orderType === 'delivery' ? 'Delivery' : 'Dine-in'
        };
        
        console.log('✅ AuthContext: Refreshed user from customer session:', customerUser);
        setUser(customerUser);
      } catch (error) {
        console.error('❌ AuthContext: Error refreshing customer session:', error);
        localStorage.removeItem('bookfood_customer_session');
        setUser(null);
      }
    } else {
      console.log('⚠️ AuthContext: No customer session to refresh');
      setUser(null);
    }
  };

  const value = {
    user,
    login,
    register,
    logout,
    refreshCustomerSession,
    isLoading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
